import axios from 'axios';

const BASE = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
  timeout: 30000,
});

let token = localStorage.getItem('wof_token');
export function setAuthToken(t) {
  token = t;
  if (t) api.defaults.headers.common.Authorization = `Bearer ${t}`;
  else delete api.defaults.headers.common.Authorization;
}
setAuthToken(token);

/* ---------------- 429 BACKOFF ---------------- */
let cooldownUntil = 0;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

api.interceptors.request.use(async (config) => {
  if (cooldownUntil > Date.now() && !config._retry) {
    await sleep(Math.min(3000, cooldownUntil - Date.now()));
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err.config || {};
    const status = err.response?.status;

    /* retry once on 429 */
    if (status === 429 && !cfg._retry) {
      cfg._retry = true;
      const retryAfter = Number(err.response.headers?.['retry-after']) || 3;
      cooldownUntil = Date.now() + retryAfter * 1000;
      await sleep(retryAfter * 1000);
      return api(cfg);
    }

    const e = new Error(
      err.response?.data?.error ||
      err.response?.data?.message ||
      (status === 429 ? 'Slow down a little.' : err.message) ||
      'Request failed');
    e.status = status;
    e.field = err.response?.data?.field;
    e.data = err.response?.data;
    return Promise.reject(e);
  }
);

/* ---------------- HELPERS ---------------- */
export const compact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(v);
};

export const money = (v, currency = 'RON') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, maximumFractionDigits: 2,
    }).format(Number(v) || 0);
  } catch {
    return `${Number(v).toFixed(2)} ${currency}`;
  }
};

export function timeAgo(d) {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(d).toLocaleDateString();
}

export default api;